'use client';

import { c as _c } from "react/compiler-runtime";
import Lottie from 'lottie-react';
import Link from 'next/link';
import line from 'public/assets/lottie/line.json';
import { forwardRef, ForwardRefRenderFunction, useLayoutEffect, useRef
// useState
} from 'react';
import css from '@/components/Services/style.module.scss';
import { ServiceItem } from '@/components/Services/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { isArrayNotEmpty } from '@/helpers/array';
import { useCustomCursor } from '@/hooks/useCustomCursor';
import useDevice from '@/hooks/useDevice';
import useLottieHandlers from '@/hooks/useLottieHandlers';
import { useSmootherReady } from '@/hooks/useSmootherReady';
import { gsap, ScrollTrigger, SplitText } from '@/lib/gsap';
interface Props {
  serviceList: ServiceItem[];
  itemHeadingTag?: 'h2' | 'h3';
}
const ServiceList: ForwardRefRenderFunction<HTMLAnchorElement, Props> = (t0, ref) => {
  const $ = _c(19);
  const {
    serviceList,
    itemHeadingTag: t1
  } = t0;
  const itemHeadingTag = t1 === undefined ? "h2" : t1;
  const t2 = useLottieHandlers();
  const t3 = useLottieHandlers();
  const t4 = useLottieHandlers();
  let t5;
  if ($[0] !== t2 || $[1] !== t3 || $[2] !== t4) {
    t5 = [t2, t3, t4];
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const lineHandlers = t5;
  const {
    setIsCursorVisible
  } = useCustomCursor();
  const device = useDevice();
  const containerRef = useRef(null);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const lineRefs = useRef(t6);
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const titleRefs = useRef(t7);
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const descRefs = useRef(t8);
  const smoother = useSmootherReady();
  let t10;
  let t9;
  if ($[7] !== smoother) {
    t9 = () => {
      if (!smoother) {
        return;
      }
      const matchMedia = gsap.matchMedia();
      const splitInstances = [];
      matchMedia.add({
        isDesktop: "(min-width: 992px)",
        isMobile: "(max-width: 991px)"
      }, context => {
        const {
          isMobile
        } = context.conditions as gsap.Conditions;
        if (isMobile) {
          return;
        }
        const ctx = gsap.context(() => {
          lineRefs.current.forEach(_temp);
        }, containerRef);
        return () => {
          ctx.revert();
          splitInstances.forEach(_temp2);
        };
      });
      return () => matchMedia.revert();
    };
    t10 = [smoother];
    $[7] = smoother;
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[10] !== device || $[11] !== itemHeadingTag || $[12] !== lineHandlers || $[13] !== ref || $[14] !== serviceList || $[15] !== setIsCursorVisible) {
    t11 = isArrayNotEmpty(serviceList) && serviceList.map((t12, idx) => {
      const {
        id,
        title,
        description,
        url
      } = t12;
      return <Link key={id} ref={serviceList.at(-1)?.id === id ? ref : null} href={url || "#"} className={css.serviceItem} target={url?.includes("http") ? "_blank" : "_self"} onMouseEnter={() => setIsCursorVisible(true)} onMouseLeave={() => setIsCursorVisible(false)}><div><Typography ref={el => {
            titleRefs.current[idx] = el;
          }} uppercase={true} className={css.serviceTitle} variant="h2" tag={itemHeadingTag}><StrapiRichContent content={title} /></Typography><div className={css.description}>{device === "desktop" && <Typography className={css.serviceNum} variant="body-m" tag="span">0{idx + 1}</Typography>}<Typography ref={el_0 => {
              descRefs.current[idx] = el_0;
            }} className={css.serviceDesc} variant="body-m" tag="div"><StrapiRichContent content={description} /></Typography></div></div>{device === "mobile" && serviceList.length === idx + 1 ? null : <Lottie lottieRef={lineHandlers[idx].lottieRef} className={css.serviceLine} animationData={line} loop={false} autoplay={false} onMouseEnter={lineHandlers[idx].playForwardAnimHandler} onMouseLeave={lineHandlers[idx].playBackAnimHandler} />}<div ref={el_1 => {
          lineRefs.current[idx] = el_1;
        }} className={css.lineWrapper} /></Link>;
    });
    $[10] = device;
    $[11] = itemHeadingTag;
    $[12] = lineHandlers;
    $[13] = ref;
    $[14] = serviceList;
    $[15] = setIsCursorVisible;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = <div ref={containerRef} className={css.service}>{t11}</div>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
};
export default forwardRef(ServiceList);
function _temp(wrapperEl) {
  if (!wrapperEl) {
    return;
  }
  gsap.fromTo(wrapperEl, {
    scaleX: 1,
    transformOrigin: "right"
  }, {
    scaleX: 0,
    duration: 1.2,
    ease: "power2.out",
    scrollTrigger: {
      trigger: wrapperEl,
      start: "top bottom",
      toggleActions: "play none none none"
    },
    onComplete: () => wrapperEl?.classList.add("hidden")
  });
}
function _temp2(split) {
  return split.revert();
}